import React, { useState, useMemo, useEffect } from 'react';
import Pagination from '../../utils/pagination2';
import Breadcrumb from '../../utils/breadcrumbs';
import { FiMapPin, FiEye, FiTrash2, FiEdit3 } from 'react-icons/fi';
import CreateGroundOffcanvas from '../../components/grounds/create-offcanvas';
import EditGroundOffcanvas from '../../components/grounds/edit-offcanvas';
import { ListLoader } from '../../utils/loader';
import ConfirmationModal from '../../ui/confirmation-modal';
import ViewGroundOffcanvas from '../../components/grounds/view-offcanvas';
import toast from 'react-hot-toast';
import api from '../../libs/axios';


enum PitchType {
    ROUGH = "rough",
    CEMENT = "cement",
    TURF = "turf",
    MATTING = "matting",
    ASTROTURF = "astroturf",
}

interface IGround {
    id: number;
    name: string;
    country: string;
    city: string;
    address?: string;
    pitch_type: string;
    lighting_available: boolean;
    capacity?: number;
    status: string;
    source?: string;
    latitude?: number;
    longitude?: number;
    image_urls?: string[];
    created_at: string;
    updated_at: string;
}

const Grounds: React.FC = () => {
    const [selectedGround, setSelectedGround] = useState<IGround | null>(null);
    const [searchParams, setSearchParams] = useState<Record<string, string>>({});
    const [showViewGroundCanvas, setShowViewGroundCanvas] = useState(false);
    const [showEditGroundCanvas, setShowEditGroundCanvas] = useState(false);
    const [showAddGroundCanvas, setShowAddGroundCanvas] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [grounds, setGrounds] = useState<IGround[]>([]);
    const [loading, setLoading] = useState(false);

    // Pagination state variables
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    });

    // Fetch grounds from API
    const fetchGrounds = async () => {
        setLoading(true);
        try {
            // Build query parameters
            const queryParams = new URLSearchParams({
                page: currentPage.toString(),
                limit: limit.toString(),
                ...searchParams
            });

            const response = await api.get(`/grounds?${queryParams.toString()}`);
            setGrounds(response.data.data.grounds || response.data.data);

            // Handle pagination data from response
            if (response.data.pagination) {
                setPagination(response.data.pagination);
            } else {
                // If no pagination in response, create basic pagination
                setPagination({
                    current_page: response.data.data.current_page || currentPage,
                    per_page: response.data.data.per_page || limit,
                    total: response.data.data.total || response.data.data.count || 0,
                    total_pages: response.data.data.total_pages || Math.ceil((response.data.data.total || response.data.data.count || 0) / limit),
                    has_next_page: response.data.data.has_next_page || false,
                    has_prev_page: response.data.data.has_prev_page || false,
                    next_page: response.data.data.next_page || null,
                    prev_page: response.data.data.prev_page || null,
                });
            }
        } catch (error: any) {
            console.error("Error fetching grounds:", error);
            toast.error("Failed to fetch grounds");
        } finally {
            setLoading(false);
        }
    };

    // Filter grounds using useMemo (client-side filtering for better UX)
    const filteredGrounds = useMemo(() => {
        return grounds.filter(ground => {
            const nameMatch = !searchParams.name ||
                ground.name.toLowerCase().includes(searchParams.name.toLowerCase());

            const cityMatch = !searchParams.city ||
                ground.city.toLowerCase().includes(searchParams.city.toLowerCase());

            const countryMatch = !searchParams.country ||
                ground.country.toLowerCase().includes(searchParams.country.toLowerCase());

            const pitchTypeMatch = !searchParams.pitch_type ||
                ground.pitch_type.toLowerCase() === searchParams.pitch_type.toLowerCase();

            const statusMatch = !searchParams.status ||
                ground.status.toLowerCase() === searchParams.status.toLowerCase();

            const lightingMatch = !searchParams.lighting_available ||
                ground.lighting_available.toString() === searchParams.lighting_available;

            return nameMatch && cityMatch && countryMatch && pitchTypeMatch && statusMatch && lightingMatch;
        });
    }, [grounds, searchParams]);

    const handleViewClick = (ground: IGround) => {
        setSelectedGround(ground);
        setShowViewGroundCanvas(true);
    };

    const handleRowClick = (ground: IGround) => {
        setSelectedGround(ground);
        setShowViewGroundCanvas(true);
    };

    // Fetch grounds on component mount and when filters change
    useEffect(() => {
        fetchGrounds();
    }, [currentPage, limit]);

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1); // Reset to first page when searching
            } else {
                fetchGrounds();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchParams]);

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams(prev => {
            const newParams = { ...prev };
            if (value) {
                newParams[column] = value;
            } else {
                delete newParams[column];
            }
            return newParams;
        });
    };

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-red-100 text-red-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'draft': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Get pitch type style
    const getPitchTypeStyle = (pitchType: string) => {
        switch (pitchType) {
            case PitchType.TURF: return 'bg-green-100 text-green-800';
            case PitchType.MATTING: return 'bg-blue-100 text-blue-800';
            case PitchType.CEMENT: return 'bg-gray-100 text-gray-800';
            case PitchType.ASTROTURF: return 'bg-stone-100 text-stone-800';
            case PitchType.ROUGH: return 'bg-amber-100 text-amber-800';
            default: return 'bg-purple-100 text-purple-800';
        }
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setPagination(prev => ({
            ...prev,
            current_page: page
        }));
    };

    // Handle delete
    const handleDeleteClick = (ground: IGround, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedGround(ground);
        setDeleteConfirmOpen(true);
    };

    // Handle edit
    const handleEditClick = (ground: IGround, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedGround(ground);
        setShowEditGroundCanvas(true);
    };

    // Handle view click with event stopping
    const handleViewClickWithStop = (ground: IGround, e: React.MouseEvent) => {
        e.stopPropagation();
        handleViewClick(ground);
    };

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedGround) {
            try {
                await api.delete(`/grounds/${selectedGround.id}`);
                setGrounds(grounds.filter(ground => ground.id !== selectedGround.id));
                toast.success(`${selectedGround.name} deleted successfully!`);
                fetchGrounds(); // Refresh the data
            } catch (err) {
                console.error('Error deleting ground:', err);
                toast.error('Failed to delete ground');
            }
            setDeleteConfirmOpen(false);
            setSelectedGround(null);
        }
    };

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(1);
        setPagination(prev => ({
            ...prev,
            per_page: newLimit,
            current_page: 1
        }));
    };

    const handleGroundCreated = () => {
        fetchGrounds(); // Refresh the list
    };

    const handleGroundUpdated = () => {
        fetchGrounds(); // Refresh the list
        setShowEditGroundCanvas(false);
        setSelectedGround(null);
    };

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Ground Management
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <button
                        onClick={() => setShowAddGroundCanvas(true)}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiMapPin className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add Ground</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Ground Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Location
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Pitch Type
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Lighting
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Capacity
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Created Date
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Ground Name"
                                        value={searchParams.name || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('name', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Location"
                                        value={searchParams.city || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('city', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.pitch_type || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('pitch_type', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="turf">Turf</option>
                                        <option value="rough">Rough</option>
                                        <option value="cement">Cement</option>
                                        <option value="matting">Matting</option>
                                        <option value="astroturf">Astroturf</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.lighting_available || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('lighting_available', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="true">Available</option>
                                        <option value="false">Not Available</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('status', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="pending">Pending</option>
                                        <option value="draft">Draft</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                                {/* <td className="px-6 py-2 border-r"></td> */}
                                {/* <td className="px-6 py-2 border-r"></td> */}
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredGrounds.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-gray-500">
                                        No grounds found
                                    </td>
                                </tr>
                            ) : (
                                filteredGrounds.map(ground => (
                                    <tr
                                        key={ground.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleRowClick(ground)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex items-center">
                                                {ground.image_urls && ground.image_urls.length > 0 && (
                                                    <img
                                                        src={ground.image_urls[0]}
                                                        alt={ground.name}
                                                        className="w-8 h-8 rounded object-cover mr-3"
                                                    />
                                                )}
                                                <div className="font-semibold">{ground.name}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="text-sm text-gray-900">{ground.city}</div>
                                            <div className="text-sm text-gray-500">{ground.country}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getPitchTypeStyle(ground.pitch_type)}`}>
                                                {ground.pitch_type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 text-xs rounded-full ${ground.lighting_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {ground.lighting_available ? 'Available' : 'Not Available'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            {ground.capacity ? ground.capacity.toLocaleString() : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(ground.status)}`}>
                                                {ground.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="text-sm text-gray-900">
                                                {ground.created_at ? new Date(ground.created_at).toLocaleDateString() : '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={(e) => handleViewClickWithStop(ground, e)}
                                                    className="text-primary-600 hover:text-primary-700"
                                                    title="View Details"
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={(e) => handleEditClick(ground, e)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Edit Ground"
                                                >
                                                    <FiEdit3 className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={(e) => handleDeleteClick(ground, e)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete Ground"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                <CreateGroundOffcanvas
                    showCreateGroundCanvas={showAddGroundCanvas}
                    setShowCreateGroundCanvas={setShowAddGroundCanvas}
                    onGroundCreated={handleGroundCreated}
                />

                {selectedGround && (
                    <EditGroundOffcanvas
                        groundId={selectedGround.id.toString()}
                        showEditGroundCanvas={showEditGroundCanvas}
                        setShowEditGroundCanvas={setShowEditGroundCanvas}
                        onGroundUpdated={handleGroundUpdated}
                    />
                )}

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Ground"
                        description="Are you sure you want to delete this ground? This action cannot be undone."
                        loading={loading}
                    />
                )}

                {selectedGround && (
                    <ViewGroundOffcanvas
                        ground={selectedGround}
                        showViewGroundCanvas={showViewGroundCanvas}
                        setShowViewGroundCanvas={setShowViewGroundCanvas}
                    />
                )}
            </div>
        </div>
    );
};

export default Grounds;